import type { DOMOutputSpec, Node, NodeSpec } from 'prosemirror-model';

import type { RtSchemaType } from '../../editor/types';
import { AlignExtension } from '../../extensions/align';
import { HtmlClassExtension } from '../../extensions/html-class';
import { NodeName } from '../../schema/nodes';

export class DocNode implements NodeSpec {
  public static readonly nodeName = NodeName.Doc;
  public static is(node: Node) {
    return node.type.name === DocNode.nodeName;
  }
  public content = 'block+';
}

export class RichDocNode implements NodeSpec {
  public static readonly nodeName = NodeName.Doc;
  public static is(node: Node) {
    return node.type.name === DocNode.nodeName;
  }
  public content = '(block | columns)+';
}

export class TextNode implements NodeSpec {
  public static readonly nodeName = NodeName.Text;
  public static is(node: Node) {
    return node.type.name === TextNode.nodeName;
  }
  public group = 'inline';
}

export class ParagraphNode implements NodeSpec {
  public static readonly nodeName = NodeName.Paragraph;
  public static is(node: Node) {
    return node.type.name === ParagraphNode.nodeName;
  }
  public static isEmpty(node: Node) {
    return node.content.size === 0;
  }

  private readonly alignExtension?: AlignExtension;
  private readonly htmlClassExtension?: HtmlClassExtension;

  // TODO - find a better way of handling attrs representing the same behaviours across multiple nodes
  //  We shouldn't need to write the same code in toDOM and parseDOM over and over, especially since more extensions
  //  may be required in the future.
  //  maybe an abstract class representing a RichNode?
  public attrs = {
    ...(this.config?.withAlign && AlignExtension.attrs),
    ...(this.config?.withHtmlClass && HtmlClassExtension.attrs),
  };
  public content = 'inline*';
  public group = 'block';
  public parseDOM = [
    {
      tag: 'p',
      getAttrs: (node: HTMLElement) => ({
        ...(this.alignExtension && this.alignExtension.parseDOMGetAttrs(node)),
        ...(this.htmlClassExtension && this.htmlClassExtension.parseDOMGetAttrs(node)),
      }),
    },
  ];

  constructor(private config?: { withAlign?: boolean; withHtmlClass?: boolean }) {
    if (config?.withAlign) this.alignExtension = new AlignExtension();
    if (config?.withHtmlClass) this.htmlClassExtension = new HtmlClassExtension();
  }

  public toDOM = (node: Node): DOMOutputSpec => {
    const minHeight = node.content.size === 0 ? 'min-height: 1.7em;' : '';
    let style = '';
    if (this.alignExtension) {
      const align = this.alignExtension.getStyleSerialization(node);
      style = align ? style + align : style;
    }
    if (minHeight) {
      style = style + minHeight;
    }
    let attributes: { [key in string]: string } = {
      ...(style ? { style } : {}),
    };
    if (this.htmlClassExtension) {
      const htmlClass = this.htmlClassExtension.getClassSerialization(node);
      if (htmlClass) {
        attributes = {
          ...attributes,
          class: htmlClass,
        };
      }
    }
    return ['p', attributes, 0];
  };
}

export class HardBreakNode implements NodeSpec {
  public static readonly nodeName = NodeName.HardBreak;
  public static is(node: Node) {
    return node.type.name === HardBreakNode.nodeName;
  }
  public inline = true;
  public group = 'inline';
  public selectable = false;
  public parseDOM = [{ tag: 'br' }];
  public toDOM = (): DOMOutputSpec => ['br'];
}

export type BasicPluginSchema = RtSchemaType<
  typeof DocNode | typeof TextNode | typeof ParagraphNode | typeof HardBreakNode
>;
